import root from "window-or-global";

import {getClientConfig} from "@atg-shared/client-config";

const getKambiWidgetApiSrc = () => {
    const widgetApiSrc = getClientConfig()?.env;

    switch (widgetApiSrc) {
        case "test3":
            return "https://kambi-widget-api.bc.ctn.kambicdn.com/kambi-widget-api.js";

        case "qa":
        case "prod":
        default:
            return "https://kambi-widget-api.bc.kambicdn.com/kambi-widget-api.js";
    }
};

export enum KambiWidgetApiGeneralKey {
    BETSLIP_OUTCOMES_ARGS = "BETSLIP_OUTCOMES_ARGS",
    UPDATE_APPEND = "UPDATE_APPEND",
    UPDATE_REPLACE = "UPDATE_REPLACE",
    TYPE_COMBINATION = "TYPE_COMBINATION",
}

export enum KambiWidgetApiRequestType {
    USER_LOGGED_IN = "USER_LOGGED_IN",
    LOGIN = "LOGIN",
    LOGOUT = "LOGOUT",
    PAGE_INFO = "PAGE_INFO",
    BETSLIP_OUTCOMES = "BETSLIP_OUTCOMES",
    BETSLIP_PREPACKS = "BETSLIP_PREPACKS",
    BETSLIP_PREPACKS_REMOVE = "BETSLIP_PREPACKS_REMOVE",
    PLACE_BET_STATE = "PLACE_BET_STATE",
    CLIENT_ODDS_FORMAT = "CLIENT_ODDS_FORMAT",
    BETSLIP_STAKE_UPDATED = "BETSLIP_STAKE_UPDATED",
    USER_DATA = "USER_DATA",
    USER_SESSION_CHANGE = "USER_SESSION_CHANGE",
    VERSIONS = "VERSIONS",
    CLIENT_CONFIG = "CLIENT_CONFIG",
}

export enum KambiWidgetApiSetKey {
    CLIENT_HIDE = "CLIENT_HIDE",
    BETSLIP_SHOW = "BETSLIP_SHOW",
    BETSLIP_HIDE = "BETSLIP_HIDE",
    BETSLIP_OUTCOMES_REMOVE = "BETSLIP_OUTCOMES_REMOVE",
    BETSLIP_MAXIMIZED = "BETSLIP_MAXIMIZED",
}

const {
    USER_LOGGED_IN,
    LOGIN,
    LOGOUT,
    PAGE_INFO,
    BETSLIP_OUTCOMES,
    BETSLIP_PREPACKS,
    BETSLIP_PREPACKS_REMOVE,
    PLACE_BET_STATE,
    CLIENT_ODDS_FORMAT,
    BETSLIP_STAKE_UPDATED,
    USER_DATA,
    USER_SESSION_CHANGE,
    VERSIONS,
    CLIENT_CONFIG,
} = KambiWidgetApiRequestType;

const {
    CLIENT_HIDE,
    BETSLIP_SHOW,
    BETSLIP_HIDE,
    BETSLIP_OUTCOMES_REMOVE,
    BETSLIP_MAXIMIZED,
} = KambiWidgetApiSetKey;

const {BETSLIP_OUTCOMES_ARGS, UPDATE_APPEND, UPDATE_REPLACE, TYPE_COMBINATION} =
    KambiWidgetApiGeneralKey;

type RequestType = {
    [USER_LOGGED_IN]: "UserLoggedIn";
    [LOGIN]: "Login";
    [LOGOUT]: "Logout";
    [PAGE_INFO]: "PageInfo";
    [BETSLIP_OUTCOMES]: "BetslipOutcomes";
    [BETSLIP_PREPACKS]: "BetslipPrepacks";
    [BETSLIP_PREPACKS_REMOVE]: "BetslipPrepacksRemove";
    [PLACE_BET_STATE]: "PlaceBetState";
    [CLIENT_ODDS_FORMAT]: "ClientOddsFormat";
    [BETSLIP_STAKE_UPDATED]: "BetslipStakeUpdated";
    [USER_DATA]: "UserData";
    [USER_SESSION_CHANGE]: "UserSessionChange";
    [VERSIONS]: "Versions";
    [CLIENT_CONFIG]: "ClientConfig";
};

type SetKey = {
    [CLIENT_HIDE]: "ClientHide";
    [BETSLIP_OUTCOMES]: "BetslipOutcomes";
    [BETSLIP_OUTCOMES_REMOVE]: "BetslipOutcomesRemove";
    [BETSLIP_PREPACKS]: "BetslipPrepacks";
    [BETSLIP_PREPACKS_REMOVE]: "BetslipPrepacksRemove";
    [BETSLIP_SHOW]: "BetslipShow";
    [BETSLIP_HIDE]: "BetslipHide";
    [BETSLIP_MAXIMIZED]: "BetslipMaximized";
};

type GeneralKey = {
    [BETSLIP_OUTCOMES_ARGS]: {
        [UPDATE_APPEND]: "append";
        [UPDATE_REPLACE]: "replace";
        [TYPE_COMBINATION]: "combination";
    };
};

// Kambi's documentation of the widget api can be found here:
// https://www.kambipartner.com/techhub/api_docs/app_apis/widget_api/widget_api_docs/api_methods

export type KambiWidgetApi = {
    subscribe: (
        callback: (
            response: Record<string, unknown>,
            kambiWidgetApi: KambiWidgetApi,
        ) => void,
    ) => {unsubscribe: () => undefined};
    request: (
        type: RequestType[keyof RequestType],
        payload?: Record<string, unknown>,
    ) => void;
    response: (response: Record<string, unknown>, kambiWidgetApi: KambiWidgetApi) => void;
    navigateClient: (
        path: string,
        widgetName?: string,
        options?: {replace?: boolean},
    ) => void;
    set: (key: SetKey[keyof SetKey], value?: any) => void;
} & RequestType &
    SetKey &
    GeneralKey;

export type GlobalKambiWidget = typeof root & {
    KambiWidget?: {
        ready: Promise<KambiWidgetApi>;
    };
};

export const isKambiLoaded = () =>
    Boolean((root as GlobalKambiWidget).KambiWidget?.ready.then);

export const getKambiWidgetApi = async () => {
    if (isKambiLoaded()) {
        return (root as GlobalKambiWidget).KambiWidget?.ready as Promise<KambiWidgetApi>;
    }

    await new Promise((resolve, reject) => {
        const script = root.document.createElement("script");

        script.src = `${getKambiWidgetApiSrc()}?cb=${Date.now()}`;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;

        root.document.head.appendChild(script);
    });

    return (root as GlobalKambiWidget).KambiWidget?.ready as Promise<KambiWidgetApi>;
};

export const getKambiState = async (requestType: KambiWidgetApiRequestType) => {
    const wapi = await getKambiWidgetApi();
    const eventType = wapi[requestType];

    let unsubscribe = () => undefined;
    const responseData = new Promise((resolve) => {
        const subscription = wapi.subscribe((response) => {
            if (response.type === eventType) {
                resolve(response.data);
            }
        });
        unsubscribe = subscription.unsubscribe;
    });

    wapi.request(eventType);
    await responseData;
    unsubscribe();

    return responseData;
};
